import { ComponentChildren, h, JSX } from "preact"
import { useEffect, useRef } from "preact/hooks"
import { classJoin } from "../helpers/classjoin"

/**
 * @group Components
 */
export interface HandleProps {
    /** Vertical mode? */
    vertical: boolean
    /** Control disabled? */
    disabled: boolean
    /** Currently dragged? */
    dragging: boolean
    /** Minimum value */
    min: number
    refFn: (node: HTMLElement | null) => void
    /** Maximum value */
    max: number
    /** Current value */
    value: number
    /** Handle index */
    index: number
    /** Handle offset, % */
    offset: number
    /** Prefix for class names */
    classesPrefix: string
    /** Label for handle */
    label: string
    /** Tip content */
    children?: ComponentChildren
}

/**
 * Range handle.
 *
 * @group Components
 */
export function Handle({
    min,
    max,
    value,
    vertical,
    disabled,
    dragging,
    index,
    offset,
    classesPrefix,
    label,
    children,
    refFn
}: HandleProps) {
    const elementRef = useRef<HTMLElement | null>(null)

    useEffect(() => {
        if (dragging && elementRef.current) {
            elementRef.current.focus()
        }
    }, [dragging])

    const style: JSX.CSSProperties = vertical
        ? {
              bottom: `${offset}%`
          }
        : {
              left: `${offset}%`
          }

    const classes = classJoin(
        {
            dragging,
            handle: true,
            [`handle-${index}`]: true
        },
        cl => `${classesPrefix}${cl}`
    )

    /**
     * Save reference to element in DOM.
     */
    function saveElement(element: HTMLElement | null): void {
        refFn(element)
        if (element) {
            elementRef.current = element
        }
    }

    return (
        <div
            class={classes}
            style={style}
            role="slider"
            tabIndex={-1}
            aria-orientation={vertical ? "vertical" : "horizontal"}
            aria-disabled={true}
            aria-valuemin={min}
            aria-valuemax={max}
            aria-valuenow={value}
            aria-label={label}
            ref={saveElement}
        >
            <span class={`${classesPrefix}tip`}>{children}</span>
        </div>
    )
}
